/**
 * ช่วงเวลาของจอสถิติ — วันตามเวลาลาว (UTC+7) เพราะ API ตัดวันตามเวลาลาว
 *
 * ปุ่มด่วนส่ง `days` (API นับย้อนจากตอนนี้) · ມື້ນີ້/YTD ส่งแค่ `from` (เที่ยงคืนวันนี้ / 1 ม.ค.
 * ตามเวลาลาว — API ใช้ "ตอนนี้" เป็นปลายช่วง) · กำหนดเองส่ง `from`/`to` เป็น
 * [00:00 วันแรก, 00:00 วันถัดจากวันสุดท้าย) — วันสุดท้ายจึงนับรวมทั้งวัน
 */

export const PRESETS = [
  { days: 7, label: "7 ວັນ" },
  { days: 30, label: "30 ວັນ" },
  { days: 90, label: "90 ວັນ" },
] as const;

export type Range =
  | { kind: "today" }
  | { kind: "ytd" }
  | { kind: "days"; days: number }
  | { kind: "custom"; from: string; to: string };

export const DAY_MS = 86_400_000;
const LAO_OFFSET_MS = 7 * 3_600_000;
/** เท่ากับเพดานของ API (analytics.maxWindowDays) */
export const MAX_DAYS = 400;

/** วันนี้ตามเวลาลาว (YYYY-MM-DD) — รับเวลามาเป็นพารามิเตอร์ จะได้ไม่เรียก Date.now() ระหว่าง render */
export const laoDay = (epochMs: number) => new Date(epochMs + LAO_OFFSET_MS).toISOString().slice(0, 10);
const laoMidnight = (iso: string) => Date.parse(`${iso}T00:00:00+07:00`);
export const shiftDay = (iso: string, n: number) => laoDay(laoMidnight(iso) + n * DAY_MS);

/** วันแรกของช่วง ມື້ນີ້/YTD — คิดจากเวลาที่ส่งเข้ามา */
function anchorDay(r: { kind: "today" | "ytd" }, nowMs: number): string {
  const today = laoDay(nowMs);
  return r.kind === "today" ? today : `${today.slice(0, 4)}-01-01`;
}

export function rangeQuery(r: Range, nowMs = Date.now()): { days?: number; from?: string; to?: string } {
  switch (r.kind) {
    case "days":
      return { days: r.days };
    case "today":
    case "ytd":
      return { from: new Date(laoMidnight(anchorDay(r, nowMs))).toISOString() };
    case "custom":
      return {
        from: new Date(laoMidnight(r.from)).toISOString(),
        to: new Date(laoMidnight(shiftDay(r.to, 1))).toISOString(),
      };
  }
}

export function rangeDays(r: Range, nowMs = Date.now()): number {
  switch (r.kind) {
    case "days":
      return r.days;
    case "today":
      return 1;
    case "ytd":
      return Math.round((laoMidnight(laoDay(nowMs)) - laoMidnight(anchorDay(r, nowMs))) / DAY_MS) + 1;
    case "custom":
      return Math.round((laoMidnight(r.to) - laoMidnight(r.from)) / DAY_MS) + 1;
  }
}

/** key ของ react-query — ช่วงเดียวกันต้องได้ key เดียวกัน */
export const rangeKey = (r: Range) =>
  r.kind === "days" ? `d${String(r.days)}` : r.kind === "custom" ? `${r.from}_${r.to}` : r.kind;

/** YYYY-MM-DD → DD/MM/YYYY */
export const dmy = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
/** YYYY-MM-DD → D/M (ป้ายแกนกราฟ) */
export const dm = (iso: string) => `${String(Number(iso.slice(8, 10)))}/${String(Number(iso.slice(5, 7)))}`;

/** ป้ายสั้นของช่วงที่เลือก */
export function rangeLabel(r: Range): string {
  switch (r.kind) {
    case "today":
      return "ມື້ນີ້";
    case "ytd":
      return "ນັບແຕ່ຕົ້ນປີ (YTD)";
    case "days":
      return `${String(r.days)} ວັນຫຼ້າສຸດ`;
    case "custom":
      return `${dmy(r.from)} – ${dmy(r.to)}`;
  }
}

/** อ่าน/เขียนช่วงลง query string — แชร์ลิงก์แล้วอีกคนเห็นช่วงเดียวกัน */
export function rangeFromParams(p: URLSearchParams, fallbackDays = 30): Range {
  const from = p.get("from");
  const to = p.get("to");
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  if (from && to && iso.test(from) && iso.test(to) && from <= to) return { kind: "custom", from, to };
  const period = p.get("period");
  if (period === "today" || period === "ytd") return { kind: period };
  const days = Number(p.get("days"));
  return { kind: "days", days: PRESETS.some((x) => x.days === days) ? days : fallbackDays };
}

export function rangeToParams(r: Range): Record<string, string> {
  switch (r.kind) {
    case "days":
      return { days: String(r.days) };
    case "custom":
      return { from: r.from, to: r.to };
    default:
      return { period: r.kind };
  }
}
